import type {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {ColumnConstraint} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import type {MultiValueFilter, MultiValueFilterKey, Operator} from 'search/SearchConstants';

/**
 * Generic, product-agnostic support for a "row-scoped entity search": a search whose picker is narrowed to
 * the entity selected in a *sibling* cell of the same row.
 *
 * The narrowing is described by a {@link RowScopedSearchConfig}: the scope entity is taken from the sibling
 * cell identified by `sourceColumnGroup`, and is expressed as a membership filter (`filterKey` / `operator`)
 * applied against that entity's id.
 *
 * The source cell is expected to hold a *bioentity* typed value; its linked entity supplies the scope id
 * (see {@link getBioentityIdFromEvaluatedCell}). A source cell holding any other kind of value contributes
 * no scoping, and the search keeps its full scope.
 *
 * A column carries a single config and therefore scopes on one sibling cell. If a future column needs to
 * scope on several sibling cells at once, this can be extended to a list: accept `Array<RowScopedSearchConfig>`
 * on the column, have {@link resolveRowScopedFilter} return every applicable filter, and spread them into the
 * merged filter list (the consumer already merges via a spread, so no structural change is required there).
 */
export interface RowScopedSearchConfig {
  /**
   * Column group of the sibling cell in the same row whose selected entity scopes this column's search. The
   * first row cell belonging to this group supplies the scope entity, and is expected to hold a bioentity
   * typed value; if the row has no such cell, or it holds no selected bioentity, no scoping filter is applied
   * and the search keeps its full scope.
   */
  sourceColumnGroup: NonNullable<ColumnGroup>;
  /**
   * Multi-value search filter key to apply against the source entity's id (e.g. `ANY_LINK_SCHEMA_FIELD`).
   * The scoping filter is always a membership filter, so only multi-value keys are valid here.
   */
  filterKey: MultiValueFilterKey;
  /** Operator for the scoping filter (e.g. `HAS_ONE_OF`). */
  operator: Operator;
}

/** Extract the linked bioentity's api id from a typed value, or null if it holds no bioentity. */
export function getBioentityIdFromTypedValue(typedValue: TypedValue | null | undefined): string | null {
  if (typedValue?.type === BaseTypedValueType.BIOENTITY && typedValue.bioentitySummary?.id) {
    return typedValue.bioentitySummary.id;
  }
  return null;
}

/** Extract the linked bioentity's api id from an evaluated cell, or null if it holds no bioentity. */
export function getBioentityIdFromEvaluatedCell(cell: EvaluatedCell | undefined): string | null {
  return getBioentityIdFromTypedValue(cell?.value);
}

/** Index of the first column in the row belonging to the given column group, or -1 if none. */
export function findColumnIndexByGroup(
  columnConstraints: Array<ColumnConstraint | null>,
  columnGroup: NonNullable<ColumnGroup>
): number {
  return columnConstraints.findIndex(
    (constraint) => constraint?.columnDefinition?.columnGroup === columnGroup
  );
}

/**
 * Build the scoping filter for a row-scoped search column, or null when no scoping should apply.
 *
 * The source cell is expected to hold a *bioentity* typed value; its linked entity id becomes the scope.
 * Returns non-null only when the row has a source cell (matching `config.sourceColumnGroup`) that currently
 * resolves to a bioentity. A missing source column, or a source cell that holds no bioentity, returns null →
 * the search keeps its full (e.g. tenant-wide) scope.
 */
export function resolveRowScopedFilter(
  config: RowScopedSearchConfig,
  params: {
    columnConstraints: Array<ColumnConstraint | null>;
    evaluatedRow: Array<EvaluatedCell>;
  }
): MultiValueFilter | null {
  const sourceColumnIndex = findColumnIndexByGroup(params.columnConstraints, config.sourceColumnGroup);
  if (sourceColumnIndex === -1) {
    return null;
  }
  const scopeEntityId = getBioentityIdFromEvaluatedCell(params.evaluatedRow[sourceColumnIndex]);
  if (scopeEntityId == null) {
    return null;
  }
  return {key: config.filterKey, operator: config.operator, value: [scopeEntityId]};
}
